import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { Link } from 'react-router-dom';
import { 
  Stethoscope,
  FileText, 
  ArrowRight,
  CheckCircle, 
  TrendingUp, 
  BarChart3,
  Clock,
  Brain,
  Shield,
  Play,
  Activity,
  Code,
  Target,
  Database,
  Settings,
  AlertCircle,
  FileCheck,
  DollarSign,
  Building2,
  Ambulance,
  HeartPulse,
  Network,
  Zap
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';
import { ProductTestimonials } from '../../shared/ProductTestimonials';
import { panaceonTestimonials } from '../../shared/testimonialData';

const Panaceon: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  

  const features = [
    {
      icon: Brain,
      title: 'Multi-Agent Coding Engine',
      description: 'Specialised ICD-10, CPT, and HCPCS agents collaborate on each chart, cross-checking each other to suggest code sets with high accuracy.'
    },
    {
      icon: Shield,
      title: 'Document Intake & Pre-Processing',
      description: 'Supports a variety of input formats—scanned PDFs, EHR exports, and structured notes—normalising them for downstream AI processing.'
    },
    {
      icon: Activity,
      title: 'AI-Assisted Coding Workspace',
      description: 'Coders see AI suggestions with explanations, references to relevant chart sections, and confidence scores, enabling faster validation and edits.'
    },
    {
      icon: FileCheck,
      title: 'Confidence Scoring & Routing',
      description: 'Charts with high-confidence codes move to auto-approval lanes, while ambiguous cases are queued for human review according to configurable thresholds.'
    },
    {
      icon: Target,
      title: 'Compliance & Policy Checks',
      description: 'Built-in rules engines and audit logic check for coding completeness, bundling, and payer-specific requirements before claims are generated.'
    },
    {
      icon: BarChart3,
      title: 'Claim Generation & Export',
      description: 'Approved codes flow directly into claim files that can be exported to billing systems, clearinghouses, or payer portals'
    },
    {
      icon: TrendingUp,
      title: '	Analytics & Quality Dashboards',
      description: 'Track coder productivity, denial trends, rework drivers, and compliance KPIs across departments and facilities'
    },
    {
      icon: Building2,
      title: 'Integration-Friendly APIs',
      description: 'Connect Panaceon to your existing EHR, HIS, and RCM systems so charts and codes flow automatically with minimal manual handling.'
    }
  ];

  const solutions = [
    {
      icon: Code,
      title: 'Hospital Coding Departments',
      description: 'Accelerate inpatient and outpatient coding while maintaining accuracy and compliance, particularly in high-volume specialties.',
      features: ['Diagnosis Coding', 'Symptom Mapping', 'Comorbidity Detection', 'Version Updates', 'Clinical Validation']
    },
    {
      icon: FileText,
      title: '	Specialty Clinics & Physician Groups',
      description: 'Support consistent, compliant coding for specialty areas—orthopaedics, cardiology, oncology, and more—where documentation patterns can be complex.',
      features: ['Procedure Coding', 'E&M Levels', 'Modifier Assignment', 'Bundling Rules', 'NCCI Edits']
    },
    {
      icon: Database,
      title: 'Revenue Cycle Management (RCM) Companies',
      description: 'Scale coding operations for multiple clients using a platform that standardises workflows, reduces turnaround times, and surfaces risk areas early.',
      features: ['DME Coding', 'Supply Codes', 'Ambulance Services', 'Drug Codes', 'Level II Codes']
    },
    {
      icon: Settings,
      title: 'Audit & Quality Review Teams',
      description: 'Use Panaceon’s explanations and logs to review high-risk charts, identify training needs, and reduce post-payment audit exposure.',
      features: ['≥90% Threshold', 'Chart Complexity', 'Documentation Quality', 'Historical Accuracy', 'Risk Assessment']
    },
    {
      icon: Zap,
      title: '	Health Systems with Distributed Sites',
      description: 'Provide a common coding intelligence layer across multiple hospitals and clinics, enabling benchmarking and shared best practices.',
      features: ['Batch Processing', 'Client Management', 'Custom Workflows', 'Denial Analytics', 'Reporting Tools']
    }
  ];

  const workflowFeatures = [
    {
      icon: FileText,
      title: 'Chart Ingestion',
      description: 'Automated ingestion of medical documents with intelligent parsing of clinical notes, discharge summaries, and physician documentation.',
      features: ['Document Parsing', 'OCR Support', 'HL7 Integration', 'FHIR Compatible', 'Multi-Format']
    },
    {
      icon: Brain,
      title: 'AI Code Suggestion',
      description: 'Autonomous coding agents analyze clinical documentation and suggest accurate ICD-10, CPT, and HCPCS codes with ≥90% precision.',
      features: ['NLP Analysis', 'Context Understanding', 'Multi-Code Support', 'Citation Links', 'Confidence Scores']
    },
    {
      icon: CheckCircle,
      title: 'Auto-Approval Path',
      description: 'High-confidence codes (≥90%) are automatically approved and processed for final billing with complete audit trails.',
      features: ['Auto Approval', 'Audit Citations', 'Compliance Checks', 'Quality Assurance', 'Billing Export']
    },
    {
      icon: AlertCircle,
      title: 'Human Review Path',
      description: 'Low-confidence or complex cases are intelligently routed to certified medical coders for manual verification and validation.',
      features: ['Smart Routing', 'Priority Queues', 'Chart Context', 'Coder Feedback', 'Learning Loop']
    }
  ];

  const compliances = [
    {
      icon: TrendingUp,
      title: 'Healthcare-Grade Security Controls',
      description: 'Strong access controls, encryption, and network protections are applied across all components handling PHI.',
    },
    {
      icon: Clock,
      title: 'Role-Based Access & Least-Privilege Design',
      description: 'Coders, auditors, administrators, and IT teams see only the data they need for their roles.',
    },
    {
      icon: DollarSign,
      title: 'Audit Trails & Traceability',
      description: 'Every code suggestion, override, and approval is logged with timestamps and user IDs, supporting internal audits and payer inquiries.',
    },
    {
      icon: Shield,
      title: 'Configurable Data Retention',
      description: 'Retention policies can be tailored to organisational and regulatory requirements, with options for automatic archival and deletion.',
    },
    {
      icon: Settings,
      title: 'Compliance-Ready Architecture',
      description: 'Designed to align with major healthcare privacy and security regulations in relevant jurisdictions, with documentation available for due diligence',
    }
  ];

  const benefits = [
    {
      icon: Building2,
      title: 'Higher Coding Accuracy & Fewer Denials',
      description: 'Multi-agent AI and compliance checks catch omissions and inconsistencies early, reducing downstream payer rejections and rework.',
      features: ['High Volume Processing', 'Multi-Specialty Support', 'Integration with EHR', 'Revenue Cycle Management']
    },
    {
      icon: Ambulance,
      title: '	Improved Throughput & Productivity',
      description: 'Coders focus on edge cases and complex decisions instead of repetitive, straightforward charts, increasing charts coded per FTE.',
      features: ['Client Portal Access', 'Batch Processing', 'Custom Workflows', 'Reporting Analytics']
    },
    {
      icon: Stethoscope,
      title: 'Stronger Revenue Integrity',
      description: 'More complete, compliant coding helps capture legitimate revenue that might otherwise be missed, while reducing risk of overcoding.',
      features: ['Easy Integration', 'Specialty Coding', 'Claim Submission', 'Denial Management']
    },
    {
      icon: HeartPulse,
      title: '	Faster Turnaround for Billing',
      description: 'Automated routing and claim generation shorten the cycle from discharge to billing, improving cash flow.',
      features: ['Claim Validation', 'Audit Support', 'Policy Compliance', 'Fraud Detection']
    },
    {
      icon: BarChart3,
      title: '		Transparent, Defensible Coding Decisions',
      description: 'Explanations and logs make it easier to stand behind coding choices during internal reviews and external audits.',
      features: ['KPI Dashboards', 'Trend Analysis', 'Custom Reports', 'Quality Metrics']
    }
  ];

  const techStack = [
    {
      icon: Brain,
      title: 'Natural Language Processing',
      description: 'Advanced NLP models trained on medical terminology, clinical documentation, and coding guidelines for precise context understanding.'
    },
    {
      icon: Network,
      title: 'Autonomous Agent Architecture',
      description: 'Multi-agent system with specialized coding agents (ICD-10, CPT, HCPCS) working collaboratively for comprehensive code assignment.'
    },
    {
      icon: Database,
      title: 'Medical Knowledge Base',
      description: 'Continuously updated database of medical codes, clinical guidelines, payer policies, and regulatory requirements.'
    },
    {
      icon: Zap,
      title: 'Real-Time Processing',
      description: 'High-performance infrastructure processing charts instantly with sub-second response times for code suggestions.'
    }
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 8 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-6 h-6 bg-cyan-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 7,
                delay: i * 0.6,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-600 neon-glow">
                  <Stethoscope className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-cyan-400 uppercase tracking-wide">Medical Coding AI</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    <span className="bg-gradient-to-r from-cyan-500 to-teal-600 bg-clip-text text-transparent">Panaceon</span>
                  </h1>
                  <p className="text-sm text-muted-foreground italic">Autonomous Medical Coding Platform</p>
                </div>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed text-justify"
              >
                Panaceon is an autonomous, agentic AI platform that streamlines medical coding from end to end. It ingests clinical charts, runs ICD-10, CPT, and HCPCS agents, and suggests accurate codes that stand up to audits. Instead of relying purely on manual coding and repeated reviews, Panaceon guides coders with AI-suggested codes, confidence scores, and clear rationales. By combining multi-agent reasoning with robust compliance checks, it helps healthcare providers improve throughput, reduce errors, and protect revenue.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-cyan-500 to-teal-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-cyan-500/25 transition-all duration-300"
                >
                  Request Demo
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Watch Overview
                </button>
              </motion.div>

              {/* Key Metrics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="grid grid-cols-3 gap-8 hidden"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-cyan-500">≥90%</div>
                  <div className="text-sm text-muted-foreground">Confidence</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-teal-400">70%</div>
                  <div className="text-sm text-muted-foreground">Time Saved</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">24/7</div>
                  <div className="text-sm text-muted-foreground">Processing</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
                  className="absolute -inset-4 bg-gradient-to-r from-cyan-500 via-teal-500 to-cyan-500 rounded-2xl blur-3xl opacity-20"
                />
                <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
                  <ImageWithFallback
                    src="/assets/Products/ai-Products/panaceon.png"
                    alt="Panaceon Medical Coding Platform"
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Core <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">Features</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Autonomous agentic AI for medical coding excellence
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-6 hover:scale-105 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-500/20 to-teal-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-cyan-400" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground text-justify">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Workflow Flowchart Section */}
      <section className="relative py-24 bg-white/[0.02] hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Intelligent <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">Workflow</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Automated coding process with confidence-based routing
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="glass-strong rounded-2xl p-8 lg:p-12 overflow-x-auto"
          >
            {/* Desktop Flowchart */}
            <div className="hidden lg:block relative min-w-[1000px]">
              {/* Main Flow Row */}
              <div className="flex items-start justify-between gap-3 mb-8">
                {/* Medical Document */}
                <div className="flex items-center gap-3">
                  <div className="glass rounded-xl p-4 border-2 border-cyan-500/30 w-32 text-center">
                    <FileText className="w-7 h-7 text-cyan-400 mx-auto mb-2" />
                    <p className="text-xs font-semibold text-white">Medical Document</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-cyan-400" />
                </div>

                {/* Code Suggestions with branching */}
                <div className="relative">
                  <div className="glass rounded-xl p-4 border-2 border-cyan-500/30 w-48">
                    <div className="flex items-center gap-2 mb-2">
                      <Activity className="w-6 h-6 text-cyan-400" />
                      <p className="text-xs font-semibold text-white">Code Suggestions</p>
                    </div>
                    <p className="text-[10px] text-muted-foreground">AI-driven ≥90% precision</p>
                  </div>
                  {/* Vertical line down for Fail path */}
                  <div className="absolute left-1/2 top-full w-px h-16 bg-cyan-400/50"></div>
                </div>

                <ArrowRight className="w-5 h-5 text-cyan-400" />

                {/* Decision - Pass */}
                <div className="flex items-center gap-3">
                  <div className="glass rounded-xl p-3 border-2 border-green-500/30 w-36">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <p className="text-xs font-semibold text-white">Pass</p>
                    </div>
                    <p className="text-[10px] text-muted-foreground">User accepts</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-green-400" />
                </div>

                {/* Auto Approved Codes */}
                <div className="relative">
                  <div className="glass rounded-xl p-3 bg-teal-500/20 border-2 border-teal-500/50 w-36 text-center">
                    <CheckCircle className="w-6 h-6 text-teal-400 mx-auto mb-1" />
                    <p className="text-xs font-semibold text-teal-400">Auto Approved</p>
                  </div>
                  {/* Vertical line up from Manual Coding */}
                  <div className="absolute left-1/2 bottom-full w-px h-16 bg-purple-400/50"></div>
                </div>

                <ArrowRight className="w-5 h-5 text-teal-400" />

                {/* Final Code Output */}
                <div className="flex items-center gap-3">
                  <div className="glass rounded-xl p-3 border-2 border-purple-500/30 w-36">
                    <Shield className="w-6 h-6 text-purple-400 mb-1" />
                    <p className="text-xs font-semibold text-white">Final Code</p>
                    <p className="text-[10px] text-muted-foreground">Compliance</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-purple-400" />
                </div>

                {/* Billing Ready */}
                <div className="glass rounded-xl p-4 bg-gradient-to-br from-cyan-500/20 to-teal-500/20 border-2 border-cyan-500/50 w-36 text-center">
                  <DollarSign className="w-7 h-7 text-cyan-400 mx-auto mb-1" />
                  <p className="text-xs font-semibold text-white">Billing Ready</p>
                  <p className="text-[10px] text-muted-foreground">Claims Gen</p>
                </div>
              </div>

              {/* Fail Path Row */}
              <div className="flex items-center justify-center gap-3" style={{ marginLeft: '240px' }}>
                {/* Decision - Fail */}
                <div className="glass rounded-xl p-3 border-2 border-red-500/30 w-36">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertCircle className="w-5 h-5 text-red-400" />
                    <p className="text-xs font-semibold text-white">Fail</p>
                  </div>
                  <p className="text-[10px] text-muted-foreground">Sent for review</p>
                </div>

                <ArrowRight className="w-5 h-5 text-red-400" />

                {/* Manual Review */}
                <div className="glass rounded-xl p-3 border-2 border-orange-500/30 w-36">
                  <Target className="w-6 h-6 text-orange-400 mb-1" />
                  <p className="text-xs font-semibold text-white">Manual Review</p>
                  <p className="text-[10px] text-muted-foreground">Coder analysis</p>
                </div>

                <ArrowRight className="w-5 h-5 text-orange-400" />

                {/* Manual Coding */}
                <div className="relative">
                  <div className="glass rounded-xl p-3 border-2 border-purple-500/30 w-36">
                    <FileCheck className="w-6 h-6 text-purple-400 mb-1" />
                    <p className="text-xs font-semibold text-white">Manual Coding</p>
                    <p className="text-[10px] text-muted-foreground">Human verified</p>
                  </div>
                  {/* Arrow pointing up */}
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <ArrowRight className="w-5 h-5 text-purple-400 -rotate-90" />
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile/Tablet Flowchart */}
            <div className="lg:hidden space-y-4">
              {/* Step 1: Medical Document */}
              <div className="glass rounded-xl p-4 border-2 border-cyan-500/30">
                <div className="flex items-center gap-3">
                  <FileText className="w-8 h-8 text-cyan-400" />
                  <div>
                    <p className="font-semibold text-white">Medical Document</p>
                    <p className="text-xs text-muted-foreground">Input medical records</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-center">
                <ArrowRight className="w-6 h-6 text-cyan-400 rotate-90" />
              </div>

              {/* Step 2: Code Suggestions */}
              <div className="glass rounded-xl p-4 border-2 border-cyan-500/30">
                <div className="flex items-center gap-3 mb-2">
                  <Activity className="w-8 h-8 text-cyan-400" />
                  <p className="font-semibold text-white">Code Suggestions</p>
                </div>
                <p className="text-sm text-muted-foreground">AI-driven coding with ≥90% precision</p>
              </div>

              <div className="flex justify-center">
                <ArrowRight className="w-6 h-6 text-cyan-400 rotate-90" />
              </div>

              {/* Decision Paths */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* Pass Path */}
                <div className="space-y-3">
                  <div className="glass rounded-xl p-4 border-2 border-green-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-6 h-6 text-green-400" />
                      <p className="font-semibold text-white">Pass</p>
                    </div>
                    <p className="text-sm text-muted-foreground">User accepts AI code</p>
                  </div>
                </div>

                {/* Fail Path */}
                <div className="space-y-3">
                  <div className="glass rounded-xl p-4 border-2 border-red-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-6 h-6 text-red-400" />
                      <p className="font-semibold text-white">Fail</p>
                    </div>
                    <p className="text-sm text-muted-foreground">Sent for review</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-center gap-8 md:gap-16">
                <ArrowRight className="w-5 h-5 text-green-400 rotate-90" />
                <ArrowRight className="w-5 h-5 text-red-400 rotate-90" />
              </div>

              {/* Convergence Point */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* Left side - Direct to Auto Approved */}
                <div className="flex justify-center">
                  <div className="w-px h-12 bg-green-400"></div>
                </div>

                {/* Right side - Manual Review Path */}
                <div className="space-y-3">
                  <div className="glass rounded-xl p-4 border-2 border-orange-500/30">
                    <Target className="w-6 h-6 text-orange-400 mb-2" />
                    <p className="font-semibold text-white text-sm">Manual Review</p>
                    <p className="text-xs text-muted-foreground">Coder analysis</p>
                  </div>
                  <div className="flex justify-center">
                    <ArrowRight className="w-5 h-5 text-orange-400 rotate-90" />
                  </div>
                  <div className="glass rounded-xl p-4 border-2 border-purple-500/30">
                    <FileCheck className="w-6 h-6 text-purple-400 mb-2" />
                    <p className="font-semibold text-white text-sm">Manual Coding</p>
                    <p className="text-xs text-muted-foreground">Human verified</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-center gap-8 md:gap-16">
                <ArrowRight className="w-5 h-5 text-green-400 rotate-90" />
                <ArrowRight className="w-5 h-5 text-purple-400 rotate-90" />
              </div>

              {/* Auto Approved Codes */}
              <div className="glass rounded-xl p-4 bg-teal-500/20 border-2 border-teal-500/50 text-center">
                <CheckCircle className="w-8 h-8 text-teal-400 mx-auto mb-2" />
                <p className="font-semibold text-teal-400">Auto Approved Codes</p>
                <p className="text-xs text-muted-foreground mt-1">Converged from both paths</p>
              </div>

              <div className="flex justify-center">
                <ArrowRight className="w-6 h-6 text-teal-400 rotate-90" />
              </div>

              {/* Final Code Output */}
              <div className="glass rounded-xl p-4 border-2 border-purple-500/30">
                <Shield className="w-8 h-8 text-purple-400 mb-2" />
                <p className="font-semibold text-white text-lg mb-1">Final Code Output</p>
                <p className="text-sm text-muted-foreground">with Compliance Check</p>
              </div>

              <div className="flex justify-center">
                <ArrowRight className="w-6 h-6 text-purple-400 rotate-90" />
              </div>

              {/* Final Output: Billing Ready */}
              <div className="glass rounded-xl p-6 bg-gradient-to-br from-cyan-500/20 to-teal-500/20 border-2 border-cyan-500/50">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-10 h-10 text-cyan-400" />
                  <div>
                    <p className="font-semibold text-white text-lg">Billing Ready</p>
                    <p className="text-sm text-muted-foreground">Claims Generation</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Specialized Coding Agents Section */}
      <section className="relative py-24 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
                <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">Solutions</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-8 hover:scale-105 transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-teal-500/20 to-cyan-500/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-7 h-7 text-teal-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl mb-2 text-white">{solution.title}</h3>
                      <p className="text-sm text-muted-foreground text-justify">{solution.description}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {solution.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground hidden">
                        <CheckCircle className="w-4 h-4 text-teal-400 flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Intelligent Workflow Section */}
      <section className="relative py-24 hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Intelligent <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Workflow</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Automated coding workflow with confidence-based routing
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {workflowFeatures.map((wf, index) => {
              const Icon = wf.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-6 hover:scale-105 transition-all duration-300"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/20 to-cyan-500/20 flex items-center justify-center">
                      <span className="font-bold text-purple-400">{index + 1}</span>
                    </div>
                    <Icon className="w-8 h-8 text-purple-400" />
                  </div>
                  <h4 className="font-semibold text-lg mb-3 text-white">{wf.title}</h4>
                  <p className="text-sm text-muted-foreground mb-4 text-justify">{wf.description}</p>
                  <div className="space-y-2">
                    {wf.features.map((feat, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                        <span className="text-xs text-muted-foreground">{feat}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-24 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Data Security & <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent"> Compliance</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {compliances.map((compliance, index) => {
              const Icon = compliance.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass rounded-xl p-6 border-l-4 border-cyan-500/50 hover:border-cyan-500 transition-all duration-300"
                >
                  <Icon className="w-8 h-8 text-cyan-400 mb-3" />
                  <h3 className="font-semibold text-lg mb-2 text-white">{compliance.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3 text-justify">{compliance.description}</p>

                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
               Platform <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent"> Benefits</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-8 hover:scale-105 transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-teal-500/20 to-cyan-500/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-7 h-7 text-teal-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl mb-2 text-white">{benefit.title}</h3>
                      <p className="text-sm text-muted-foreground text-justify">{benefit.description}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 hidden">
                    {benefit.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-teal-400 flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technology Stack Section */}
      <section className="relative py-24 bg-white/[0.02] hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Technology <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Stack</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Enterprise-grade AI infrastructure
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {techStack.map((tech, index) => {
              const Icon = tech.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-6 hover:scale-105 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500/20 to-cyan-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-purple-400" />
                  </div>
                  <h4 className="font-semibold text-lg mb-3 text-white">{tech.title}</h4>
                  <p className="text-sm text-muted-foreground text-justify">{tech.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative py-24 hidden">
        <ProductTestimonials 
          testimonials={panaceonTestimonials}
          title="Medical Coding Success"
          subtitle="See how healthcare organizations transformed their coding operations"
          gradientFrom="#00CFFF"
          gradientVia="#0FFFA0"
          gradientTo="#00CFFF"
          iconColor="#00CFFF"
          dotColor="bg-cyan-500"
          speed={60}
          direction="left"
        />
      </section>

      {/* CTA Section */}
      <section className="relative py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-strong rounded-2xl p-12 text-center border border-cyan-500/30 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-teal-500/10" />
            <div className="relative z-10">
              <h2 className="text-3xl lg:text-4xl mb-4 text-white">
                Transform Your <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">Medical Coding</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto text-justify">
                Ready to achieve ≥90% confidence scoring with autonomous agentic AI? Schedule a demo to see how Panaceon can revolutionize your medical coding operations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-cyan-500 to-teal-600 text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all duration-300"
                >
                  Schedule Demo
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl font-semibold border border-cyan-500/30 hover:border-cyan-500/50 transition-all duration-300 text-white"
                >
                  Download Whitepaper
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Panaceon;